import React, { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../axios';
import Alert, { EAlertColor } from '../../components/UI/Alert/Alert';
import Button, { EButtonColor, EButtonSize } from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import Input, { EInputType } from '../../components/UI/Input/Input';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import { useAuthUser } from '../../hooks/useAuthUser';
import { parseAgentTaskDetails, getDetailsSummary } from '../../shared/agent-task-utils';

const WorkbookContextView: React.FC<{ context: string }> = ({ context }) => {
  const [showFull, setShowFull] = React.useState(false);
  
  if (context.length <= 1000) {
    return (
      <pre style={{ margin: 0, padding: '0.85rem', backgroundColor: '#1e293b', color: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
        {context}
      </pre>
    );
  }
  
  return (
    <div>
      <pre style={{ margin: 0, padding: '0.85rem', backgroundColor: '#1e293b', color: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto', maxHeight: showFull ? 'none' : '200px' }}>
        {showFull ? context : `${context.substring(0, 1000)}\n\n... [Tekstiä lyhennetty, yhteensä ${context.length} merkkiä]`}
      </pre>
      <button 
        type="button" 
        onClick={(e) => { e.stopPropagation(); setShowFull(!showFull); }} 
        style={{
          marginTop: '0.5rem',
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: 'bold',
          padding: 0,
          textDecoration: 'underline'
        }}
      >
        {showFull ? 'Näytä vähemmän ▲' : 'Näytä koko teksti ▼'}
      </button>
    </div>
  );
};

interface IAgentTask {
  id: string;
  taskType: string;
  targetEntityId?: string;
  status: string;
  details?: string;
  operatorId?: string;
  created?: number;
  updated?: number;
  duration?: number;
  promptTokens?: number;
  candidateTokens?: number;
  totalTokens?: number;
}

const AgentTasks: React.FC<RouteComponentProps> = ({ history }) => {
  const [tasks, setTasks] = useState<IAgentTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [expandedTaskIds, setExpandedTaskIds] = useState<Record<string, boolean>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [taskType, setTaskType] = useState('RESEARCH_GATEKEEPER');
  const [targetEntityId, setTargetEntityId] = useState('');
  const [details, setDetails] = useState('');

  const authUser = useAuthUser();
  const { sort, onSort, items: sortedTasks } = useTableSort({ data: tasks });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/json/agent-tasks');
      setTasks(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data || "Virhe ladattaessa agent-tehtäviä");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleTrigger = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post('/json/agent-tasks', {
        action: 'trigger'
      });
      const sweptCount = response.data.sweptCount || 0;
      setSuccess(`Tehtäväajuri käynnistetty onnistuneesti! Siirretty jonoon ${sweptCount} tehtävää.`);
      fetchTasks();
    } catch (err: any) {
      setError(err.response?.data || "Virhe käynnistettäessä tehtäväajuria");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskType) return;
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.post('/json/agent-tasks', {
        action: 'add',
        taskType,
        targetEntityId: targetEntityId ? parseInt(targetEntityId) : undefined,
        details
      });
      setSuccess("Uusi tehtävä lisätty onnistuneesti jonoon!");
      setTargetEntityId('');
      setDetails('');
      setShowAddForm(false);
      fetchTasks();
    } catch (err: any) {
      setError(err.response?.data || "Virhe lisättäessä uutta tehtävää");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän tehtävän? Se poistetaan lopullisesti tietokannasta.")) return;
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.post('/json/agent-tasks', {
        action: 'delete',
        id: taskId
      });
      setSuccess("Tehtävä poistettu onnistuneesti.");
      fetchTasks();
    } catch (err: any) {
      setError(err.response?.data || "Virhe poistettaessa tehtävää");
    } finally {
      setActionLoading(false);
    }
  };

  if (authUser && !authUser.isPrimary) {
    return <Container><Alert>Ei käyttöoikeutta</Alert></Container>;
  }

  const getStatusBadge = (status: string) => {
    let backgroundColor = '#e5e7eb';
    let color = '#374151';

    switch (status) {
      case 'PENDING':
        backgroundColor = '#fef3c7';
        color = '#d97706';
        break;
      case 'PROCESSING':
        backgroundColor = '#dbeafe';
        color = '#2563eb';
        break;
      case 'COMPLETED':
        backgroundColor = '#d1fae5';
        color = '#059669';
        break;
      case 'FAILED':
        backgroundColor = '#fee2e2';
        color = '#dc2626';
        break;
      case 'FLAGGED':
        backgroundColor = '#f3e8ff';
        color = '#7c3aed';
        break;
    }

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        backgroundColor,
        color
      }}>
        {status}
      </span>
    );
  };

  return (
    <Container>
      {error && <Alert>{error}</Alert>}
      {success && <Alert color={EAlertColor.SUCCESS}>{success}</Alert>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2>Agent-tehtävien hallinta</h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
            {authUser?.isPrimary ? "Ylläpitäjän näkymä: Kaikki tehtävät." : "Operaattorin näkymä: Vain oman siilosi tehtävät."}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button color={showAddForm ? EButtonColor.SECONDARY : undefined} onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? 'Peruuta' : 'Uusi Tehtävä'}
          </Button>
          <Button onClick={handleTrigger} disabled={actionLoading}>
            {actionLoading ? 'Ajetaan...' : 'Käynnistä Tehtäväajuri (Sweep) ⚡'}
          </Button>
        </div>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddTask} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Luo uusi Agent-tehtävä</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '600px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>Tehtävän tyyppi / Agentti</label>
              <select 
                value={taskType} 
                onChange={(e) => setTaskType(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '1rem' }}
              >
                <option value="RESEARCH_GATEKEEPER">RESEARCH_GATEKEEPER (Monivaiheinen tutkimus - Portinvartija)</option>
                <option value="RESEARCH_FOCUS">RESEARCH_FOCUS (Teematutkimus)</option>
                <option value="NIGHTLY_AUDIT">NIGHTLY_AUDIT (Automaattinen laskutus/vertailu-auditointi)</option>
                <option value="B2B_OUTREACH">B2B_OUTREACH (Yrittäjien kontaktointi)</option>
              </select>
            </div>

            <Input
              type={EInputType.number}
              inputName="targetEntityId"
              label="Kohde-entiteetin ID (esim. MarketResearchTask ID / Invoice ID)"
              placeholder="Vapaaehtoinen ID-numero..."
              value={targetEntityId}
              onChange={val => setTargetEntityId(val as string)}
            />

            <Input
              type={EInputType.textarea}
              inputName="details"
              label="Tehtävän lisätiedot / AI-suoritusohjeet"
              placeholder="Esim. fysiikan alan tarkempi rajaus, lisätietoja..."
              value={details}
              onChange={val => setDetails(val as string)}
            />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <Button onClick={(e) => handleAddTask(e as any)} type="submit" disabled={actionLoading}>Luo tehtävä jonoon (PENDING)</Button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <Spinner />
      ) : tasks.length > 0 && sortedTasks ? (
        <Table hover sortContext={{ sort, onSort }}>
          <thead>
            <tr>
              <TableTh sort="taskType">Tehtävätyyppi</TableTh>
              <TableTh sort="targetEntityId">Kohde ID</TableTh>
              <TableTh sort="status">Tila</TableTh>
              <TableTh sort="duration">Kesto</TableTh>
              <TableTh sort="totalTokens">Poletit (in/out)</TableTh>
              <TableTh sort="details">Tiedot</TableTh>
              <TableTh sort="created">Luotu</TableTh>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <React.Fragment key={task.id}>
                <tr onClick={() => setExpandedTaskIds(prev => ({ ...prev, [task.id]: !prev[task.id] }))} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 'bold' }}>{task.taskType}</td>
                  <td>{task.targetEntityId || "-"}</td>
                  <td>{getStatusBadge(task.status)}</td>
                  <td style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {task.duration != null ? `${task.duration.toFixed(2)}s` : "-"}
                  </td>
                  <td style={{ fontSize: '0.875rem' }}>
                    {task.totalTokens != null 
                      ? `${task.promptTokens?.toLocaleString()} / ${task.candidateTokens?.toLocaleString()} (${task.totalTokens?.toLocaleString()})` 
                      : "-"
                    }
                  </td>
                  <td style={{ fontSize: '0.875rem', color: '#4b5563', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getDetailsSummary(task.details)}>
                    {getDetailsSummary(task.details)}
                  </td>
                  <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {task.created ? new Date(task.created).toLocaleString("fi-FI") : "-"}
                  </td>
                </tr>
                {expandedTaskIds[task.id] && (
                  <tr>
                    <td colSpan={7} style={{ backgroundColor: '#f8fafc', padding: '1.25rem', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                        {(() => {
                          const parsedDetails = parseAgentTaskDetails(task.details);
                          const hasInputs = parsedDetails.customQuery || parsedDetails.workbookContext || parsedDetails.plainText;
                          return (
                            <>
                              {hasInputs && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                  {parsedDetails.customQuery && (
                                    <div>
                                      <strong style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Mukautettu kysymys / AI-ohjeet</strong>
                                      <pre style={{ margin: 0, padding: '0.85rem', backgroundColor: '#1e293b', color: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                                        {parsedDetails.customQuery}
                                      </pre>
                                    </div>
                                  )}
                                  {parsedDetails.workbookContext && (
                                    <div>
                                      <strong style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Lähdeaineisto ({parsedDetails.workbookContext.length} merkkiä)</strong>
                                      <WorkbookContextView context={parsedDetails.workbookContext} />
                                    </div>
                                  )}
                                  {parsedDetails.plainText && (
                                    <div>
                                      <strong style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Tehtävän tiedot</strong>
                                      <pre style={{ margin: 0, padding: '0.85rem', backgroundColor: '#1e293b', color: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                                        {parsedDetails.plainText}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!hasInputs && (
                                <div>
                                  <strong style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Yksityiskohdat</strong>
                                  <pre style={{ margin: 0, padding: '0.85rem', backgroundColor: '#1e293b', color: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                                    Ei lisätietoja saatavilla.
                                  </pre>
                                </div>
                              )}
                              {task.status === 'FAILED' && (
                                <div style={{ borderLeft: '4px solid #ef4444', paddingLeft: '0.75rem', marginTop: '0.25rem' }}>
                                  <strong style={{ display: 'block', fontSize: '0.75rem', color: '#ef4444', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Virheilmoitus</strong>
                                  <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: 'bold', whiteSpace: 'pre-wrap' }}>
                                    {parsedDetails.errorMessage || 'Tehtäväajon suoritus epäonnistui. Lisätietoja ei ole saatavilla.'}
                                  </span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                          {task.targetEntityId && (task.taskType.startsWith("RESEARCH_") || task.taskType === "RESEARCH_FOCUS") && (
                            <Button size={EButtonSize.SMALL} onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/market-research/${task.targetEntityId}`);
                            }}>
                              Avaa AI-raportti ja verkkolähteet markkinatutkimuksessa 🔍
                            </Button>
                          )}
                          <Button size={EButtonSize.SMALL} color={EButtonColor.DANGER} onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}>
                            Poista Tehtävä
                          </Button>
                        </div>
                      </div>                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
          <p style={{ margin: 0, color: '#6b7280' }}>Ei agent-tehtäviä siilossasi.</p>
        </div>
      )}
    </Container>
  );
};

export default AgentTasks;
